import type { CountryField } from '@payloadcms/plugin-form-builder/types'
import type { Control, FieldErrorsImpl } from 'react-hook-form'

import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import React, { useState, useRef, useEffect } from 'react'
import { Controller } from 'react-hook-form'
import { ChevronDown, Search } from 'lucide-react'

import { Error } from '../Error'
import { Width } from '../Width'
import { countryOptions } from './options'
import { TypedLocale } from 'payload'

export const Country: React.FC<
  CountryField & {
    control: Control
    errors: Partial<FieldErrorsImpl>
    locale: TypedLocale
  }
> = ({ name, control, errors, label, required, width, locale }) => {
  const [isOpen, setIsOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCountry, setSelectedCountry] = useState<{ label: string; value: string } | null>(
    null,
  )
  const dropdownRef = useRef<HTMLDivElement>(null)

  const filteredCountries = countryOptions.filter((country) =>
    country.label.toLowerCase().includes(searchTerm.toLowerCase()),
  )

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
        setSearchTerm('')
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  return (
    <Width width={width}>
      <Label className="" htmlFor={name}>
        {label}

        {required && (
          <span className="required">
            * <span className="sr-only">(required)</span>
          </span>
        )}
      </Label>
      <Controller
        control={control}
        defaultValue=""
        name={name}
        render={({ field: { onChange, value } }) => {
          const currentCountry = countryOptions.find((t) => t.value === value)

          return (
            <div className="relative" ref={dropdownRef}>
              <button
                type="button"
                className="focus:outline-hidden flex h-10 w-full items-center justify-between rounded border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                onClick={() => setIsOpen(!isOpen)}
                id={name}
              >
                <span className={currentCountry ? 'text-foreground' : 'text-muted-foreground'}>
                  {currentCountry?.label || label}
                </span>
                <ChevronDown
                  className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                />
              </button>

              {isOpen && (
                <div className="absolute z-50 mt-1 w-full rounded-md border border-input bg-background shadow-lg">
                  <div className="border-b border-border p-2">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                      <Input
                        type="text"
                        placeholder="Search countries..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="h-8 pl-9 text-sm"
                        autoFocus
                      />
                    </div>
                  </div>

                  <div className="max-h-60 overflow-y-auto">
                    {filteredCountries.length > 0 ? (
                      filteredCountries.map((country) => (
                        <button
                          key={country.value}
                          type="button"
                          className="w-full px-3 py-2 text-left text-sm hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground focus:outline-none"
                          onClick={() => {
                            onChange(country.value)
                            setSelectedCountry(country)
                            setIsOpen(false)
                            setSearchTerm('')
                          }}
                        >
                          {country.label}
                        </button>
                      ))
                    ) : (
                      <div className="px-3 py-2 text-sm text-muted-foreground">
                        No countries found
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )
        }}
        rules={{ required }}
      />
      {errors[name] && <Error name={name} locale={locale} />}
    </Width>
  )
}
